import { ElementProps } from '@/components/sections/builder/types';
import { BlogPostSubtitleElementDataType } from '@/components/sections/builder/elements/body/blog-post/type';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { Type } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';

const defaultData: BlogPostSubtitleElementDataType = {
  name: 'Sub Title',
  label:
    'We use an agile approach to test assumptions and connect with the needs of your audience early and often.',
  textStyle: 'body',
  padding: {
    bottom: 1,
    top: 1,
    left: 1,
    right: 1,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as BlogPostSubtitleElementDataType;

  return (
    <>
      <p
        data-animation='true'
        {...getOverrideAnimationParams(data.animation)}
        className={clsx(
          'font-light ',
          'p-3',
          'lg:pb-[--p-bottom] lg:pl-[--p-left] lg:pr-[--p-right] lg:pt-[--p-top]',
          data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
          data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
          data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
        )}
        style={
          {
            '--p-top': `${data.padding?.top || 0}px`,
            '--p-bottom': `${data.padding?.bottom || 0}px`,
            '--p-left': `${data.padding?.left || 0}px`,
            '--p-right': `${data.padding?.right || 0}px`,
          } as React.CSSProperties
        }
      >
        {data.label}
      </p>
    </>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as BlogPostSubtitleElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Section Header</h1>
      <div className='mb-3 flex flex-col gap-2'>
        <h4>Text</h4>
        <Textarea
          name='label'
          defaultValue={defaultData.label}
          value={data.label}
          className='w-full'
          placeholder='Label'
          maxLength={100}
          rows={4}
          onChange={(e) =>
            builder.setChildData(element.parentId as string, element.id, {
              label: e.target.value,
            })
          }
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle || defaultData.textStyle}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              textStyle: v,
            })
          }
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <PaddingSetting
        value={data.padding}
        hideLeft={true}
        hideRight={true}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const BlogPostSubtitle = {
  type: 'blog_post_subtitle',
  group: 'blog_post',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default BlogPostSubtitle;
